// Form logic for the admin model registry.
//
// The api_key rules are the subtle part: it is write-only, an update that omits it keeps the
// stored key, and "" clears it. Keeping that out of the dialog makes it testable.

export const PROVIDERS = [
  { value: "azure_openai", label: "Azure OpenAI" },
  { value: "openai", label: "OpenAI" },
];

export const PROVIDER_LABELS = PROVIDERS.reduce((labels, provider) => {
  labels[provider.value] = provider.label;
  return labels;
}, {});

export const isAzureProvider = (provider) => provider === "azure_openai";

const trim = (value) => (typeof value === "string" ? value.trim() : "");

/** A blank create form; Azure OpenAI is the documented default provider. */
export const emptyModelForm = (overrides = {}) => ({
  name: "",
  provider: "azure_openai",
  endpoint: "",
  apiVersion: "",
  deployment: "",
  apiKey: "",
  isActive: true,
  clearApiKey: false,
  clientId: "",
  ...overrides,
});

/** Map a record from the API into form state. The key is never pre-filled — it can't be read. */
export const formFromModel = (model) => ({
  name: model?.name ?? "",
  provider: model?.provider ?? "azure_openai",
  endpoint: model?.endpoint ?? "",
  apiVersion: model?.api_version ?? "",
  deployment: model?.deployment ?? "",
  apiKey: "",
  isActive: model?.is_active !== false,
  clearApiKey: false,
  clientId: model?.client_id ? String(model.client_id) : "",
});

/** Global (admin-level) models have no client and are visible to every client. */
export const isGlobalModel = (model) => !model?.client_id;

export const isClientModel = (model, clientId) =>
  Boolean(clientId) && String(model?.client_id) === String(clientId);

/** Visible at the client level: the client's own models plus the globals. */
export const isVisibleToClient = (model, clientId) =>
  isGlobalModel(model) || isClientModel(model, clientId);

/**
 * Whether the current scope may change a model.
 *
 * Admin scope may edit anything. Client scope may only edit its own; admin-level (global)
 * models are read-only there.
 */
export const canEditModel = (model, { scope = "admin", clientId = null } = {}) => {
  if (scope === "admin") return true;
  return isClientModel(model, clientId);
};

/** A model has a usable stored key unless it has none, or the user asked to clear it. */
export const hasUsableStoredKey = (model, form) =>
  Boolean(model?.api_key_masked) && !form?.clearApiKey;

/**
 * Validate the form.
 *
 * The api_key requirement only applies when no usable key already exists — on create, or on
 * edit of a model with no stored key.
 *
 * @returns {{ok: boolean, errors: object}} Errors keyed by field so they can be shown inline.
 */
export const validateModelForm = (form = {}, { isEdit = false, model = null } = {}) => {
  const errors = {};

  if (!trim(form.name)) {
    errors.name = "name is required.";
  }

  if (!PROVIDERS.some((provider) => provider.value === form.provider)) {
    errors.provider = "provider must be 'azure_openai' or 'openai'.";
  }

  const keyProvided = Boolean(trim(form.apiKey));
  const storedKeyUsable = isEdit && hasUsableStoredKey(model, form);

  if (isAzureProvider(form.provider)) {
    const hasEndpoint = Boolean(trim(form.endpoint));

    if (!hasEndpoint) {
      errors.endpoint = "azure_openai endpoints require 'endpoint' and 'api_key'.";
    }
    if (!keyProvided && !storedKeyUsable) {
      errors.apiKey = "azure_openai endpoints require 'endpoint' and 'api_key'.";
    }
    if (!trim(form.deployment)) {
      errors.deployment = "azure_openai endpoints require a 'deployment' name.";
    }
  } else if (!keyProvided && !storedKeyUsable) {
    errors.apiKey = "openai endpoints require an 'api_key'.";
  }

  return { ok: Object.keys(errors).length === 0, errors };
};

/**
 * Build the POST/PUT body.
 *
 * - `api_key` is omitted when kept, sent when typed, and sent as "" when clearing.
 * - `client_id` is the owning client, or null for a global (admin-level) model. Sending the
 *   current owner explicitly keeps it stable on update; sending null makes it global.
 * - `endpoint` is sent for either provider: optional for OpenAI, where it points at an
 *   OpenAI-compatible gateway or proxy. `api_version` stays Azure-only.
 */
export const buildModelPayload = (form = {}) => {
  const azure = isAzureProvider(form.provider);

  const payload = {
    name: trim(form.name),
    provider: form.provider,
    endpoint: trim(form.endpoint) || null,
    api_version: azure ? trim(form.apiVersion) || null : null,
    deployment: trim(form.deployment) || null,
    is_active: Boolean(form.isActive),
    client_id: trim(form.clientId) || null,
  };

  if (form.clearApiKey) {
    payload.api_key = "";
  } else if (trim(form.apiKey)) {
    payload.api_key = form.apiKey;
  }
  // else: omitted on purpose so the stored key is kept.

  return payload;
};

/** Body for the ad-hoc test endpoint (no name / is_active). An OpenAI endpoint is honoured. */
export const buildTestPayload = (form = {}) => {
  const azure = isAzureProvider(form.provider);

  return {
    provider: form.provider,
    endpoint: trim(form.endpoint) || null,
    api_version: azure ? trim(form.apiVersion) || null : null,
    deployment: trim(form.deployment) || null,
    api_key: form.apiKey,
  };
};

/** True when the form can be tested on its own (an unregistered endpoint with a key). */
export const canTestFromForm = (form = {}) => Boolean(trim(form.apiKey)) && Boolean(trim(form.deployment));

/** Human summary of a test response. */
export const describeTestResult = (result) => {
  if (!result) return "";
  if (result.success) {
    const provider = PROVIDER_LABELS[result.provider] ?? result.provider ?? "";
    return `Connection OK${provider ? ` (${provider})` : ""}${result.deployment ? ` · ${result.deployment}` : ""}${result.response ? ` · "${result.response}"` : ""}`;
  }
  return result.message || "The test request failed.";
};

/**
 * Picker options for a registered model, safest defaults for reference fields.
 *
 * Inactive models are left out — selection is what `is_active` governs. The value is the
 * model `name`, which is what `llmNode.model` and the generator's `model` parameter expect.
 *
 * @param {object[]} models Records from GET /admin/models.
 * @returns {{value: string, label: string, provider: string, deployment: string}[]}
 */
export const buildModelOptions = (models = []) => {
  const seen = new Set();

  return (models || [])
    .filter((model) => model && model.is_active !== false)
    .map((model) => {
      const value = String(model.name ?? model.public_id ?? "");
      if (!value || seen.has(value)) return null;
      seen.add(value);

      const detail = [PROVIDER_LABELS[model.provider] ?? model.provider, model.deployment]
        .filter(Boolean)
        .join(" · ");

      return {
        value,
        label: detail ? `${value} — ${detail}` : value,
        provider: model.provider ?? null,
        deployment: model.deployment ?? null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.value.localeCompare(b.value));
};

/**
 * Models every LLM node offers before the registry ones. The first is the default for a new
 * node, so an agent answers with a known-good deployment when nobody picks anything.
 */
export const BUILT_IN_LLM_MODELS = ["Agentwire-GPT-5.3", "Agentwire-GPT-4o"];

export const DEFAULT_LLM_MODEL = BUILT_IN_LLM_MODELS[0];

/**
 * Options for an LLM node's model field: the built-ins first, then registered endpoints
 * (skipping any that duplicate a built-in name).
 */
export const buildLlmModelOptions = (models = []) => {
  const items = Array.isArray(models) ? models.filter(Boolean) : [];

  // Accept registry records *or* ready-made picker options: a caller that has already run
  // them through buildModelOptions would otherwise contribute nothing, because options have
  // `value` but no `name`, and every entry would be dropped.
  const alreadyOptions =
    items.length > 0 &&
    items.every((item) => !item.name && !item.public_id && typeof item.value === "string");

  const registered = alreadyOptions
    ? items.map((item) => ({ value: item.value, label: item.label ?? item.value }))
    : buildModelOptions(items);

  const builtInNames = new Set(BUILT_IN_LLM_MODELS.map((name) => name.toLowerCase()));

  return [
    ...BUILT_IN_LLM_MODELS.map((name) => ({ value: name, label: name })),
    ...registered.filter((option) => !builtInNames.has(option.value.toLowerCase())),
  ];
};

/**
 * Which model an LLM node's field should show.
 *
 * The graph data seeds the field once; after that the field belongs to the user. Without this
 * split, an emptied field is re-filled from the graph on the next render, which looked like
 * the value "springing back".
 *
 * @param {object} options
 * @param {boolean} options.firstPass True only for the first sync of this node instance.
 * @param {string} [options.graphModel] The model stored on the node data.
 * @param {string} [options.currentModel] What the field currently shows.
 * @param {string} [options.defaultModel]
 * @returns {string}
 */
export const resolveNodeModel = ({
  firstPass,
  graphModel,
  currentModel,
  defaultModel = DEFAULT_LLM_MODEL,
}) => {
  if (!firstPass) {
    return currentModel ?? "";
  }
  return graphModel || defaultModel;
};

const REASONING_KEYS = [
  "reasoning",
  "reasoning_content",
  "reasoningContent",
  "reasoning_text",
  "thinking",
  "thought",
  "thoughts",
  "chain_of_thought",
  "chainOfThought",
  "analysis",
];

const toReasoningText = (value) => {
  if (typeof value === "string") return value.trim() ? value : null;

  if (Array.isArray(value)) {
    const parts = value
      .map((entry) => (typeof entry === "string" ? entry : entry?.text ?? entry?.content ?? ""))
      .filter((entry) => typeof entry === "string" && entry.trim());
    return parts.length ? parts.join("\n") : null;
  }

  if (value && typeof value === "object") {
    const text = value.text ?? value.content ?? value.summary;
    return typeof text === "string" && text.trim() ? text : null;
  }

  return null;
};

/**
 * Pull a reasoning / chain-of-thought explanation out of a model test response.
 *
 * Reasoning models expose it under different names (and sometimes nested), so this checks the
 * common ones and returns the first usable string, or null.
 */
export const readReasoning = (result) => {
  if (!result || typeof result !== "object") return null;

  const candidate = result.data ?? result.raw ?? result.result ?? null;

  for (const key of REASONING_KEYS) {
    const found = toReasoningText(result[key]) ?? (candidate ? toReasoningText(candidate[key]) : null);
    if (found) return found;
  }

  return null;
};

/**
 * Build the prompt the registry's `/test` endpoint expects from a conversation so far.
 * The endpoint is stateless, so the transcript is sent with each turn.
 */
export const buildChatPrompt = (history = [], nextMessage = "") => {
  const lines = (history || [])
    .filter((message) => message.role === "user" || message.role === "assistant")
    .map((message) => `${message.role}: ${message.content}`);

  return [...lines, `user: ${nextMessage}`, "assistant:"].join("\n");
};

/** Detail line for a selected model (used under the picker). */
export const describeModelOption = (models = [], name) => {
  const match = (models || []).find((model) => String(model.name ?? model.public_id) === String(name));
  if (!match) return "";
  return [PROVIDER_LABELS[match.provider] ?? match.provider, match.deployment, match.api_key_masked]
    .filter(Boolean)
    .join(" · ");
};
